import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { AppDispatch } from "../../../core/data/redux/store";
import {
  getStaffQuarterApprovalConfig,
  saveStaffQuarterApprovalConfig,
  getStaffQuarterPools,
  getWillingnessApproversByPool,
  updateApproverConfigByPool,
  getLocationApprovers,
  updateApproverConfigByLocation,
  getStaffQuarterLocations,
  getRegistrationApprovers,
} from "../../../core/data/redux/staffQuarterSlice";

const padEmpNo = (v?: string | null): string => {
  const t = (v ?? "").trim();
  return t ? t.padStart(8, "0") : "";
};

interface ApproverDetail {
  empId: string;
  name: string;
  designation: string;
  stage?: number;
}

const StaffQuarterApprovalSettings: React.FC = () => {
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();

  const [activeAppId, setActiveAppId] = useState<string>("1");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Filter States
  const [regEmpId, setRegEmpId] = useState<string>("00015433");

  const [pools, setPools] = useState<any[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<string>("5");

  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("1");

  // Dynamic list of approver employee IDs (length 1 to 7)
  const [approvers, setApprovers] = useState<string[]>(["", ""]);
  // Additional approver details (name, designation, stage) fetched from API
  const [approverDetails, setApproverDetails] = useState<ApproverDetail[]>([]);

  const appTypes = [
    { id: "1", name: "Registration Approvers" },
    { id: "2", name: "Willingness Approvers" },
    { id: "3", name: "Occupation Approvers" },
    { id: "4", name: "Vacation Approvers" },
  ];

  // Fetch Pools & Locations master data
  useEffect(() => {
    dispatch(getStaffQuarterPools({})).then((res: any) => {
      const poolData = res?.payload?.data || res?.payload || [];
      if (Array.isArray(poolData) && poolData.length > 0) {
        setPools(poolData);
        if (!selectedPoolId) {
          setSelectedPoolId(String(poolData[0].id || poolData[0].pool_type_id || "5"));
        }
      }
    });

    dispatch(getStaffQuarterLocations({})).then((res: any) => {
      const locData = res?.payload?.data || res?.payload || [];
      if (Array.isArray(locData) && locData.length > 0) {
        setLocations(locData);
        if (!selectedLocationId) {
          setSelectedLocationId(String(locData[0].id || locData[0].code || "1"));
        }
      }
    });
  }, [dispatch]);

  const fetchConfig = async (appId: string, poolIdVal?: string, locIdVal?: string, empIdVal?: string) => {
    setLoading(true);
    setIsEditing(false);
    setEditingIndex(null);
    try {
      let response: any;
      if (appId === "1") {
        const targetEmp = empIdVal || regEmpId;
        response = await dispatch(getRegistrationApprovers(targetEmp)).unwrap();
      } else if (appId === "2") {
        const pid = poolIdVal || selectedPoolId;
        response = await dispatch(getWillingnessApproversByPool(pid)).unwrap();
      } else if (appId === "3" || appId === "4") {
        const lid = locIdVal || selectedLocationId || "1";
        const targetAppId = (appId === "3" || appId === "4") ? "4" : appId;
        response = await dispatch(getLocationApprovers({ appId: targetAppId, locationId: lid })).unwrap();
      } else {
        response = await dispatch(getStaffQuarterApprovalConfig(appId)).unwrap();
      }

      let list: any[] = [];
      const rootRes = response?.data || response;

      if (Array.isArray(rootRes?.data?.data?.approvers)) {
        list = rootRes.data.data.approvers;
      } else if (Array.isArray(rootRes?.data?.approvers)) {
        list = rootRes.data.approvers;
      } else if (Array.isArray(rootRes?.approvers)) {
        list = rootRes.approvers;
      } else if (Array.isArray(rootRes?.data?.data)) {
        list = rootRes.data.data;
      } else if (Array.isArray(rootRes?.data)) {
        list = rootRes.data;
      } else if (Array.isArray(rootRes)) {
        list = rootRes;
      }

      if (Array.isArray(list) && list.length > 0 && (list[0].empId !== undefined || list[0].stage !== undefined || list[0].name !== undefined)) {
        const sorted = [...list].sort((a, b) => (Number(a.stage) || 0) - (Number(b.stage) || 0));
        const fetchedApprovers = sorted.map((item) => String(item.empId || ""));
        const details: ApproverDetail[] = sorted.map((item) => ({
          empId: String(item.empId || ""),
          name: item.name || item.employeeName || item.empName || "",
          designation: item.designation || item.designationName || "",
          stage: item.stage,
        }));

        setApproverDetails(details);

        const activeLevelsCount = Math.max(2, fetchedApprovers.length);
        const activeApprovers: string[] = [];
        for (let i = 0; i < activeLevelsCount; i++) {
          activeApprovers.push(fetchedApprovers[i] || "");
        }
        setApprovers(activeApprovers);
        return;
      }

      // Fallback for single config object with approver1...approver7
      const fetchedData = Array.isArray(rootRes?.data) ? rootRes.data[0] : (rootRes?.data || rootRes);

      if (fetchedData && typeof fetchedData === "object") {
        const fetchedApprovers: string[] = [];
        for (let i = 1; i <= 7; i++) {
          const val = fetchedData[`approver${i}`];
          fetchedApprovers.push(val !== null && val !== undefined ? String(val) : "");
        }

        let lastNonEmptyIndex = -1;
        for (let i = 6; i >= 0; i--) {
          if (fetchedApprovers[i]?.trim()) {
            lastNonEmptyIndex = i;
            break;
          }
        }

        const activeLevelsCount = Math.max(2, lastNonEmptyIndex + 1);
        const activeApprovers = fetchedApprovers.slice(0, activeLevelsCount);
        setApprovers(activeApprovers);
        setApproverDetails([]);
      } else {
        setApprovers(["", ""]);
        setApproverDetails([]);
      }
    } catch (error) {
      console.error("Failed to fetch approval config", error);
      setApprovers(["", ""]);
      setApproverDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig(
      activeAppId,
      activeAppId === "2" ? selectedPoolId : undefined,
      activeAppId === "3" || activeAppId === "4" ? selectedLocationId : undefined,
      activeAppId === "1" ? regEmpId : undefined
    );
  }, [activeAppId, selectedPoolId, selectedLocationId]);

  const handleApproverChange = (index: number, val: string) => {
    const updated = [...approvers];
    updated[index] = val;
    setApprovers(updated);
  };

  const handleApproverBlur = (index: number, val: string) => {
    const updated = [...approvers];
    updated[index] = val.trim() ? padEmpNo(val) : "";
    setApprovers(updated);
  };

  const addApproverLevel = () => {
    if (approvers.length < 7) {
      setApprovers([...approvers, ""]);
      setIsEditing(true);
      setEditingIndex(approvers.length);
    } else {
      toast.info("Maximum 7 approvers allowed.");
    }
  };

  const removeApproverLevel = (index: number) => {
    if (approvers.length > 1) {
      const updated = [...approvers];
      updated.splice(index, 1);
      setApprovers(updated);
      setApproverDetails((prev) => prev.filter((_, i) => i !== index));
      setIsEditing(true);
    } else {
      toast.warning("At least one level of approval is required.");
    }
  };

  const handleSave = async () => {
    const filledApproversCount = approvers.filter((a) => a.trim()).length;
    if (filledApproversCount === 0) {
      return toast.warning("Please enter at least one valid Approver Employee ID.");
    }

    setSaving(true);

    try {
      let response: any;

      if (activeAppId === "2") {
        const currentPoolObj = pools.find((p) => String(p.id) === String(selectedPoolId));
        const poolNameStr = currentPoolObj?.namePool || currentPoolObj?.poolName || currentPoolObj?.pool_name || `Pool #${selectedPoolId}`;

        const payload: any = {
          poolId: Number(selectedPoolId) || 5,
          appId: Number(activeAppId) || 2,
          payscale: "ALL",
          description: `Willingness approval config for pool type ${poolNameStr.toLowerCase()}`,
        };

        for (let i = 1; i <= 7; i++) {
          const val = (approvers[i - 1] || "").trim();
          payload[`approver${i}`] = val ? Number(val) : null;
        }

        response = await dispatch(updateApproverConfigByPool(payload)).unwrap();
      } else if (activeAppId === "3" || activeAppId === "4") {
        const targetAppId = (activeAppId === "3" || activeAppId === "4") ? 4 : Number(activeAppId);
        const payload: any = {
          locationId: Number(selectedLocationId) || 1,
          appId: targetAppId,
          payscale: "ALL",
          description: `${activeAppId === "3" ? "Occupation" : "Vacation"} approval config for location ${selectedLocationId}`,
        };

        for (let i = 1; i <= 7; i++) {
          const val = (approvers[i - 1] || "").trim();
          payload[`approver${i}`] = val ? Number(val) : null;
        }

        response = await dispatch(updateApproverConfigByLocation(payload)).unwrap();
      } else {
        const payload: any = {
          description: `${appTypes.find((a) => a.id === activeAppId)?.name} config`,
          appId: activeAppId,
        };

        if (regEmpId.trim()) {
          payload.reqEmp = padEmpNo(regEmpId.trim());
        }

        for (let i = 1; i <= 7; i++) {
          const val = (approvers[i - 1] || "").trim();
          payload[`approver${i}`] = val ? padEmpNo(val) : null;
        }

        response = await dispatch(saveStaffQuarterApprovalConfig(payload)).unwrap();
      }

      const resData = response?.data || response;
      if (
        response?.success ||
        resData?.success ||
        response?.message?.includes("successfully") ||
        resData?.message?.includes("successfully") ||
        response?.id ||
        resData?.id
      ) {
        toast.success("Approval Configuration saved successfully.");
        setIsEditing(false);
        setEditingIndex(null);
        fetchConfig(
          activeAppId,
          activeAppId === "2" ? selectedPoolId : undefined,
          activeAppId === "3" || activeAppId === "4" ? selectedLocationId : undefined,
          activeAppId === "1" ? regEmpId : undefined
        );
      } else {
        toast.error(response?.message || resData?.message || "Failed to save configuration.");
      }
    } catch (error: any) {
      toast.error(error || "An error occurred while saving the configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-2">
            <div className="my-auto">
              <h3 className="mb-0 fs-18 fw-bold">Staff Quarter Approval Settings</h3>
              <nav>
                <ol className="breadcrumb mb-0 fs-12">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Staff Quarter Management</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Approval Settings
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons ms-2">
              <CollapseHeader />
            </div>
          </div>

          {/* Service Type Selection Chips */}
          <div className="card border-0 shadow-sm mb-3 rounded-3">
            <div className="card-body py-2 px-3">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <span className="fw-semibold text-muted me-2 fs-11 text-uppercase">
                  SERVICE TYPE:
                </span>

                {appTypes.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    className={`btn btn-sm ${
                      activeAppId === app.id
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => setActiveAppId(app.id)}
                    style={{
                      borderRadius: 20,
                      padding: "4px 14px",
                      fontSize: "13px",
                    }}
                  >
                    {app.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-3">
                <div className="card-body p-3">

                  {/* Relevant filter for the selected service type */}
                  <div className="row g-3 mb-4">
                    {activeAppId === "1" && (
                      <div className="col-md-5 col-lg-4">
                        <label className="form-label fw-semibold fs-12">
                          Employee ID <span className="text-danger">*</span>
                        </label>

                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            inputMode="numeric"
                            value={regEmpId}
                            placeholder="e.g. 00015433"
                            onChange={(e) => setRegEmpId(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                fetchConfig(
                                  "1",
                                  undefined,
                                  undefined,
                                  e.currentTarget.value
                                );
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value) {
                                setRegEmpId(padEmpNo(value));
                              }
                            }}
                          />

                          <button
                            className="btn btn-outline-primary btn-sm"
                            type="button"
                            onClick={() =>
                              fetchConfig(
                                "1",
                                undefined,
                                undefined,
                                regEmpId
                              )
                            }
                            disabled={loading}
                          >
                            <i className="ti ti-search me-1" />
                            Fetch
                          </button>
                        </div>

                        <div className="text-muted fs-11 mt-1">
                          Employee for whom the registration approval chain is
                          configured.
                        </div>
                      </div>
                    )}

                    {activeAppId === "2" && (
                      <div className="col-md-5 col-lg-4">
                        <label className="form-label fw-semibold fs-12">
                          Pool Type <span className="text-danger">*</span>
                        </label>

                        <select
                          className="form-select form-select-sm"
                          value={selectedPoolId}
                          onChange={(e) => setSelectedPoolId(e.target.value)}
                        >
                          {pools.length > 0 ? (
                            pools.map((pool: any) => (
                              <option
                                key={pool.id ?? pool.pool_type_id}
                                value={pool.id ?? pool.pool_type_id}
                              >
                                {pool.namePool ||
                                  pool.poolName ||
                                  pool.pool_name ||
                                  pool.name ||
                                  `Pool #${pool.id ?? pool.pool_type_id}`}
                              </option>
                            ))
                          ) : (
                            <option value="5">Civil (ID: 5)</option>
                          )}
                        </select>
                      </div>
                    )}

                    {(activeAppId === "3" || activeAppId === "4") && (
                      <div className="col-md-5 col-lg-4">
                        <label className="form-label fw-semibold fs-12">
                          Location <span className="text-danger">*</span>
                        </label>

                        <select
                          className="form-select form-select-sm"
                          value={selectedLocationId}
                          onChange={(e) =>
                            setSelectedLocationId(e.target.value)
                          }
                        >
                          {locations.length > 0 ? (
                            locations.map((location: any) => {
                              const locationId =
                                location.id ?? location.code;

                              return (
                                <option
                                  key={locationId}
                                  value={locationId}
                                >
                                  {location.name ||
                                    location.location_name ||
                                    location.locationName ||
                                    location.code ||
                                    `Location #${location.id}`}
                                  {location.code
                                    ? ` (${location.code})`
                                    : ""}
                                </option>
                              );
                            })
                          ) : (
                            <option value="1">Location #1</option>
                          )}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Section heading */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 className="mb-0 fw-semibold text-dark">
                        <i className="ti ti-settings-cog me-2 text-primary" />
                        Approvers Chain
                      </h5>

                      <span className="text-muted fs-11">
                        Configure the sequential approval path for{" "}
                        {appTypes.find((a) => a.id === activeAppId)?.name}.
                        Maximum 7 levels.
                      </span>
                    </div>

                    {!loading && approvers.length < 7 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 py-1 px-3"
                        onClick={addApproverLevel}
                      >
                        <i className="ti ti-plus" />
                        Add Approver
                      </button>
                    )}
                  </div>

                  {loading ? (
                    <div className="text-center py-5 text-muted">
                      <div
                        className="spinner-border text-primary mb-2"
                        role="status"
                        style={{ width: 24, height: 24 }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="small">
                        Loading approval configuration...
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive rounded-3 border">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th
                                style={{ width: "80px" }}
                                className="text-center"
                              >
                                Stage
                              </th>
                              <th style={{ width: "240px" }}>
                                Employee ID
                              </th>
                              <th>Approver Name</th>
                              <th>Designation</th>
                              <th
                                style={{ width: "120px" }}
                                className="text-center"
                              >
                                Action
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {approvers.map((empId, index) => {
                              const detail = approverDetails[index];

                              const hasDetail =
                                !!detail &&
                                (!!detail.name || !!detail.designation) &&
                                (!empId ||
                                  detail.empId === empId ||
                                  padEmpNo(detail.empId) === padEmpNo(empId) ||
                                  detail.empId.toString().replace(/^0+/, '') === empId.toString().replace(/^0+/, ''));

                              const isRowEditable =
                                isEditing || editingIndex === index;

                              return (
                                <tr key={`approver-${index}`}>
                                  <td className="text-center">
                                    <span
                                      className="badge bg-primary text-white rounded-circle"
                                      style={{
                                        width: 26,
                                        height: 26,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 12,
                                      }}
                                    >
                                      {index + 1}
                                    </span>
                                  </td>

                                  <td>
                                    <input
                                      type="text"
                                      className={`form-control form-control-sm ${
                                        !isRowEditable
                                          ? "bg-light border-0 fw-semibold text-dark"
                                          : ""
                                      }`}
                                      inputMode="numeric"
                                      value={empId}
                                      disabled={!isRowEditable}
                                      placeholder={`Approver ${
                                        index + 1
                                      } ID`}
                                      onChange={(e) =>
                                        handleApproverChange(
                                          index,
                                          e.target.value
                                        )
                                      }
                                      onBlur={(e) =>
                                        handleApproverBlur(
                                          index,
                                          e.target.value
                                        )
                                      }
                                    />
                                  </td>

                                  <td>
                                    {hasDetail && detail?.name ? (
                                      <span className="fw-semibold text-dark">
                                        {detail.name}
                                      </span>
                                    ) : (
                                      <span className="text-muted fs-12 fst-italic">
                                        -
                                      </span>
                                    )}
                                  </td>

                                  <td>
                                    {hasDetail && detail?.designation ? (
                                      <span className="badge bg-light text-primary border px-2 py-1 fs-12">
                                        {detail.designation}
                                      </span>
                                    ) : (
                                      <span className="text-muted fs-12">
                                        -
                                      </span>
                                    )}
                                  </td>

                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      <button
                                        type="button"
                                        className={`btn btn-sm p-1 border-0 ${
                                          isRowEditable
                                            ? "btn-primary text-white"
                                            : "btn-light text-primary"
                                        }`}
                                        onClick={() => {
                                          setIsEditing(true);
                                          setEditingIndex(index);
                                        }}
                                        title="Edit Row"
                                      >
                                        <i
                                          className="ti ti-pencil"
                                          style={{ fontSize: 16 }}
                                        />
                                      </button>

                                      {approvers.length > 1 && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-light text-danger p-1 border-0"
                                          onClick={() =>
                                            removeApproverLevel(index)
                                          }
                                          title="Delete Row"
                                        >
                                          <i
                                            className="ti ti-trash"
                                            style={{ fontSize: 16 }}
                                          />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {approvers.length === 0 && (
                        <div className="text-center text-muted py-4 fs-12">
                          No approver levels configured.
                        </div>
                      )}

                      {/* Footer actions */}
                      <div className="d-flex align-items-center justify-content-end mt-4 pt-3 border-top">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm px-3 me-2"
                          onClick={() =>
                            fetchConfig(
                              activeAppId,
                              activeAppId === "2"
                                ? selectedPoolId
                                : undefined,
                              activeAppId === "3" ||
                                activeAppId === "4"
                                ? selectedLocationId
                                : undefined,
                              activeAppId === "1"
                                ? regEmpId
                                : undefined
                            )
                          }
                          disabled={saving || loading}
                        >
                          <i className="ti ti-refresh me-1" />
                          Reset
                        </button>

                        <button
                          type="button"
                          className="btn btn-primary btn-sm px-4"
                          onClick={handleSave}
                          disabled={saving || loading}
                        >
                          <i className="ti ti-device-floppy me-1" />
                          {saving ? "Saving..." : "Save Configuration"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffQuarterApprovalSettings;